import "./style.scss";
import {
  TextField,
  Typography,
  List,
  ListItem,
  InputAdornment,
  Card,
  Button,
  Fab,
  Autocomplete,
  IconButton,
  Avatar,
  Box,
} from "@mui/material";
import { isValid } from "date-fns";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { TimePicker } from "@mui/x-date-pickers/TimePicker";
import SaveIcon from "@mui/icons-material/Save";
import { Formik } from "formik";
import axios from "axios";
import { useEffect, useState, useCallback } from "react";
import {
  deleteProfileImage,
  getProfileData,
  updateProfileData,
  uploadImages,
} from "../../../api/api";
import Loading from "../../../ui-components/Loding/Loading";
import { ToastContainer, toast, Zoom } from "react-toastify";
import DropZone from "./DropZone";
import ImageGrid from "./ImageGrid";
import { ls } from "../../../utils/localStorage";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import { ClearIcon } from "@mui/x-date-pickers";
import { religionsList } from "../../../utils/dropDownValues";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import DeleteIcon from "@mui/icons-material/Delete";

dayjs.extend(customParseFormat);
const MAX_PROFILE_IMAGES = 2;
const MAX_PROFILE_IMAGE_SIZE_MB = 15;
const MAX_PROFILE_IMAGE_SIZE_BYTES = MAX_PROFILE_IMAGE_SIZE_MB * 1024 * 1024;

const SearchableDropdown = ({
  name,
  label,
  options,
  value,
  onChange,
  onBlur,
  getOptionLabel = (option) => option?.name || "",
  disabled = false,
  noOptionsText = "No options",
  className,
}) => {
  return (
    <Autocomplete
      size="small"
      fullWidth
      className={className}
      options={options || []}
      value={value || null}
      onChange={(_, selectedValue) => onChange(selectedValue)}
      onBlur={onBlur}
      getOptionLabel={getOptionLabel}
      isOptionEqualToValue={(option, selected) =>
        String(option?.id) === String(selected?.id)
      }
      disabled={disabled}
      noOptionsText={noOptionsText}
      renderInput={(params) => (
        <TextField {...params} name={name} label={label} />
      )}
    />
  );
};

const EditProfile = () => {
  const [loading, setLoading] = useState(true);
  const [dropdownOptions, setDropdownOptions] = useState(null);
  const [states, setStates] = useState(null);
  const [enableSaveButton, setEnableSaveButton] = useState(false);
  const [isTimeOfBirthPickerOpen, setIsTimeOfBirthPickerOpen] = useState(false);

  const [formData, setFormData] = useState({
    about_me: "",
    address: "",
    age: "",
    body_type: "",
    caste: "",
    citizen: "",
    city: "",
    country: "",
    dob: null,
    drinking_habit: "",
    eating_habit: "",
    education: "",
    email_id: "",
    employeed_in: "",
    end_date: null,
    family_status: "",
    family_type: "",
    fathers_occupation: "",
    gender: "",
    height: "",
    hobbies: "",
    interests: "",
    marital_status: "",
    mobileno: "",
    mother_tongue: "",
    mothers_occupation: "",
    name: "",
    no_of_brothers: "",
    no_of_brothers_married: "",
    no_of_sisters: "",
    no_of_sisters_married: "",
    occupation: "",
    physical_status: "",
    pincode: "",
    profession: "",
    profile_creater: "",
    profile_id: "",
    referral_code: "",
    religion: "",
    require_details: "",
    salary: "",
    smoking_habit: "",
    star: "",
    start_date: null,
    state: "",
    sub_caste: "",
    surname: "",
    time_of_birth: null,
    weight: "",
    zodiac: "",
  });

  const [images, setImages] = useState([]);

  const [previewUrl, setPreviewUrl] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);

  const onDrop = useCallback(
    (acceptedFiles) => {
      console.log(acceptedFiles, "acceptedFiles");
      if (!acceptedFiles) return;

      const files = Array.isArray(acceptedFiles)
        ? acceptedFiles
        : acceptedFiles instanceof FileList
          ? Array.from(acceptedFiles)
          : [acceptedFiles];

      if (images.length >= MAX_PROFILE_IMAGES) {
        toast.info(`Only ${MAX_PROFILE_IMAGES} profile images are allowed`, {
          position: "top-right",
          autoClose: 1500,
          theme: "colored",
          transition: Zoom,
        });
        return;
      }

      const allowedFiles = files.slice(0, MAX_PROFILE_IMAGES - images.length);
      if (allowedFiles.length < files.length) {
        toast.info(`Only ${MAX_PROFILE_IMAGES} profile images are allowed`, {
          position: "top-right",
          autoClose: 1500,
          theme: "colored",
          transition: Zoom,
        });
      }

      const validFiles = allowedFiles.filter((file) => {
        const isValidSize = file.size <= MAX_PROFILE_IMAGE_SIZE_BYTES;

        if (!isValidSize) {
          toast.error(
            `Each image must be ${MAX_PROFILE_IMAGE_SIZE_MB}MB or less`,
            {
              position: "top-right",
              autoClose: 1500,
              theme: "colored",
              transition: Zoom,
            },
          );
        }

        return isValidSize;
      });

      validFiles.forEach((file) => {
        const reader = new FileReader();
        const formData = new FormData();
        formData.append("images", file, file.name);
        reader.onload = function (e) {
          setImages((prevState) => [...prevState, e.target.result]);
        };
        reader.readAsDataURL(file);
        uploadImage(formData);
      });
    },
    [images],
  );

  const getImageId = (image) => {
    if (!image) return null;

    if (typeof image === "object") {
      return image.image_id || image.id || image.imageId || null;
    }

    if (typeof image !== "string") return null;
    if (image.startsWith("data:")) return null;

    try {
      const parsed = new URL(image);
      const imageIdFromQuery =
        parsed.searchParams.get("image_id") || parsed.searchParams.get("id");
      if (imageIdFromQuery) return imageIdFromQuery;

      const pathParts = parsed.pathname.split("/").filter(Boolean);
      return pathParts[pathParts.length - 1] || null;
    } catch (error) {
      return image;
    }
  };

  const removeFile = (file) => async () => {
    const imageId = getImageId(file);

    try {
      if (imageId) {
        const response = await deleteProfileImage(imageId);
        if (response?.status !== 200 && response?.status !== 204) {
          throw new Error("Unable to delete image");
        }
      }

      setEnableSaveButton(true);
      setImages((prevImages) => prevImages.filter((item) => item !== file));
      toast.success("Image deleted successfully", {
        position: "top-right",
        autoClose: 1500,
        theme: "colored",
        transition: Zoom,
      });
    } catch (error) {
      toast.error(
        error?.response?.data?.error?.message || "Failed to delete image",
        {
          position: "top-right",
          autoClose: 1500,
          theme: "colored",
          transition: Zoom,
        },
      );
    }
  };
  const uploadImage = async (formData) => {
    try {
      setLoading(true);
      const response = await uploadImages(formData);
      if (response && response.status == 200) {
        setLoading(false);
        toast.success(response.data.message, {
          position: "top-right",
          autoClose: 1500,
          theme: "colored",
          transition: Zoom,
        });
      }
    } catch (error) {
      if (error?.response?.status === 413) {
        toast.error(
          `Image is too large. Please upload a file up to ${MAX_PROFILE_IMAGE_SIZE_MB}MB`,
          {
            position: "top-right",
            autoClose: 1500,
            theme: "colored",
            transition: Zoom,
          },
        );
        setLoading(false);
        return;
      }

      toast.error(
        error?.response?.data?.error?.message || "Something went wrong",
        {
          position: "top-right",
          autoClose: 1500,
          theme: "colored",
          transition: Zoom,
        },
      );
      setLoading(false);
    }
  };

  const handleChangeStatus = ({ meta }, status) => {
    console.log(status, meta);
  };

  const fetchDropdownsValues = async () => {
    const data = JSON.parse(ls.getItem("dropdown_values_for_reference"));
    setDropdownOptions(data);
  };

  const fetchStates = async () => {
    const data = JSON.parse(ls.getItem("states_for_reference"));
    setStates(data);
  };

  useEffect(() => {
    fetchDropdownsValues();
    fetchStates();
    fetchProfileData();
  }, []);

  const parseTimeOfBirth = (value) => {
    if (!value) return null;
    const parsed = dayjs(value, ["hh:mm A", "HH:mm"], true);
    if (parsed.isValid()) return parsed;
    const fallback = dayjs(value);
    return fallback.isValid() ? fallback : null;
  };

  const fetchProfileData = async () => {
    try {
      setLoading(true);
      const response = await getProfileData();
      if (response && response.data) {
        console.log(dayjs(response.data.time_of_birth), "profile data");
        setFormData({
          ...formData,
          ...response.data,
          time_of_birth: parseTimeOfBirth(response.data.time_of_birth),

          dob: response.data.dob ? dayjs(response.data.dob) : null,
          end_date: response.data.end_date
            ? dayjs(response.data.end_date)
            : null,
          start_date: response.data.start_date
            ? dayjs(response.data.start_date)
            : null,
        });
        setImages((response.data.images || []).slice(0, MAX_PROFILE_IMAGES));
        setLoading(false);
        setEnableSaveButton(false);
      }
    } catch (error) {
      toast.error(
        error?.response?.data?.error?.message || "Something wend wrong",
        {
          position: "top-right",
          autoClose: 1500,
          theme: "colored",
          transition: Zoom,
        },
      );
      setLoading(false);
    }
  };

  const updateProfile = async (data) => {
    try {
      setLoading(true);
      delete data.images;
      const response = await updateProfileData(data);
      console.log(response, "response");
      if (response.status === 204) {
        toast.success("Profile updated successfully", {
          position: "top-right",
          autoClose: 1500,
          theme: "colored",
          transition: Zoom,
        });
        fetchProfileData();
        setLoading(false);
      }
    } catch (error) {
      console.log(error);
      toast.error(
        error?.response?.data?.error?.message || "Something wend wrong",
        {
          position: "top-right",
          autoClose: 1500,
          theme: "colored",
          transition: Zoom,
        },
      );
      setLoading(false);
    }
  };

  const getSelectedOptionById = (options, selectedId) => {
    if (selectedId === undefined || selectedId === null || selectedId === "") {
      return null;
    }

    return (
      options?.find((option) => String(option.id) === String(selectedId)) ||
      null
    );
  };

  return (
    <div
      className="container-fluid edit-profile"
      style={{
        overflow: loading ? "hidden" : "auto",
        height: loading ? "calc(100vh - 112px)" : "auto",
      }}
    >
      <div>
        <h1>Edit Profile</h1>
        <br />
        {dropdownOptions ? (
          <Formik
            enableReinitialize={true}
            initialValues={formData}
            validate={(values, initialValues) => {
              // if(JSON.stringify(values) === JSON.stringify(formData)){
              // setEnableSaveButton(false);
              // }
              // else {
              setEnableSaveButton(true);
              // }
              const errors = {};
              return errors;
            }}
            onSubmit={(values, { setSubmitting }) => {
              const data = { ...values };
              console.log(data, formData, "data");
              Object.keys(data).forEach((e) => {
                if (data[e] == null) {
                  data[e] = "";
                }
              });
              let payload = { ...formData, ...data };

              console.log(payload, "payload before update", formData, data);
              payload.height = payload.height ? parseFloat(payload.height) : 0;
              payload.weight = payload.weight ? parseFloat(payload.weight) : 0;
              payload.no_of_sisters_married = parseFloat(
                payload.no_of_sisters_married
                  ? payload.no_of_sisters_married
                  : 0,
              );
              payload.no_of_brothers_married = parseFloat(
                payload.no_of_brothers_married
                  ? payload.no_of_brothers_married
                  : 0,
              );
              payload.no_of_brothers = parseFloat(
                payload.no_of_brothers ? payload.no_of_brothers : 0,
              );
              payload.no_of_sisters = parseFloat(
                payload.no_of_sisters ? payload.no_of_sisters : 0,
              );
              payload.gender = payload.gender.toString();
              payload.body_type = payload.body_type.toString();
              payload.caste = String(payload.caste ?? "");
              payload.citizen = payload.citizen.toString();
              payload.country = payload.country.toString();
              payload.drinking_habit = payload.drinking_habit.toString();
              payload.eating_habit = payload.eating_habit.toString();
              payload.education = payload.education.toString();
              payload.family_status = payload.family_status.toString();
              payload.family_type = payload.family_type.toString();
              payload.marital_status = payload.marital_status.toString();
              payload.mother_tongue = payload.mother_tongue.toString();
              payload.occupation = payload.occupation.toString();
              payload.physical_status = payload.physical_status.toString();
              payload.profession = payload.profession.toString();
              payload.profile_creater = payload.profile_creater.toString();
              payload.religion = String(payload.religion ?? "");
              payload.salary = payload.salary.toString();
              payload.smoking_habit = payload.smoking_habit.toString();
              payload.star = payload.star.toString();
              payload.zodiac = payload.zodiac.toString();
              payload.state = payload.state.toString();
              payload.district = payload.district.toString();
              payload.images = images;

              if (payload.time_of_birth) {
                const timeValue = dayjs(payload.time_of_birth);
                payload.time_of_birth = timeValue.isValid()
                  ? timeValue.format("hh:mm A")
                  : payload.time_of_birth;
              } else {
                payload.time_of_birth = "";
              }
              console.log(payload.time_of_birth, "time of birth");

              delete payload.id;
              delete payload.created_by;
              delete payload.updated_by;
              delete payload.created_at;
              delete payload.updated_at;
              delete payload.is_membership;
              delete payload.paid_status;
              delete payload.paid_date;
              delete payload.start_date;
              delete payload.end_date;
              delete payload.forget_hash;
              delete payload.forget_hash_expiry;

              console.log(payload, "payload");
              updateProfile(payload);
            }}
          >
            {({
              values,
              errors,
              touched,
              handleChange,
              handleBlur,
              setFieldValue,
              handleSubmit,
            }) => {
              const casteOptions = values?.religion
                ? (
                    religionsList.find(
                      (option) => String(option.id) === String(values.religion),
                    )?.castes || []
                  ).map((item) => ({ id: item.id, name: item.caste }))
                : [];

              const districtOptions =
                states?.DISTRICTS?.filter(
                  (obj) => String(values.state) === String(obj.state_id),
                ) || [];

              return (
                <form onSubmit={handleSubmit}>
                  <Fab
                    type="submit"
                    className="save_btn"
                    variant="extended"
                    color="primary"
                    disabled={!enableSaveButton}
                  >
                    <SaveIcon /> Save Changes
                  </Fab>
                  <div className="row">
                    <div className="row  mb-3">
                      <div className="col-sm-12">
                        <Typography gutterBottom variant="h5" component="div">
                          Profile Images
                        </Typography>
                      </div>
                      <div className="col-sm-12">
                        {/* {images.length < 5 ? (
                      <DropZone onDrop={onDrop} accept={"image/*"} />
                    ) : (
                      ""
                    )} */}
                        {/* <TextField
                        type="file"
                        onChange={(e) => onDrop(e.target.files)}
                        accept="image/*"
                        inputProps={{ multiple: true }}
                        helperText={`Max ${MAX_PROFILE_IMAGES} images`}
                        variant="outlined"
                        disabled={images.length >= MAX_PROFILE_IMAGES}
                      /> */}

                        {/* Upload Button */}
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            // alignItems: "center",
                          }}
                        >
                          <Button
                            component="label"
                            variant="contained"
                            startIcon={<CloudUploadIcon />}
                            sx={{ width: 200 }}
                            disabled={images.length >= MAX_PROFILE_IMAGES}
                          >
                            Upload Image
                            <TextField
                              type="file"
                              accept="image/*"
                              hidden
                              onChange={(e) => onDrop(e.target.files)}
                              inputProps={{ multiple: true }}
                              helperText={`Max ${MAX_PROFILE_IMAGES} images`}
                              variant="outlined"
                            />
                          </Button>
                          <Typography
                            sx={{ mt: 1 }}
                            variant="caption"
                            color="text.secondary"
                          >
                            {images.length} of {MAX_PROFILE_IMAGES} images
                            uploaded
                          </Typography>
                          <Typography
                            sx={{ mt: 0.5 }}
                            variant="caption"
                            color="text.secondary"
                          >
                            Max file size: {MAX_PROFILE_IMAGE_SIZE_MB}MB per
                            image
                          </Typography>
                        </Box>
                        <Box
                          sx={{
                            display: "flex",
                            flexWrap: "wrap",
                            gap: 2,
                            mt: 2,
                          }}
                        >
                          {images &&
                            images.map((image) => (
                              <ImageGrid
                                images={image}
                                removeFile={removeFile}
                              />
                            ))}
                        </Box>

                        {/* {images && images.length > 0 && (
                        <ImageGrid images={images} removeFile={removeFile} />
                      )} */}
                      </div>
                    </div>
                    <div className="row">
                      <div className="col-sm-12">
                        <Typography gutterBottom variant="h5" component="div">
                          Personal Information
                        </Typography>
                      </div>
                    </div>
                    <div className="col-sm-6">
                      <List>
                        <ListItem className="row">
                          <div className="col-sm-4">
                            <Typography
                              variant="subtitle1"
                              color="text.secondary"
                              component="div"
                            >
                              Name:
                            </Typography>
                          </div>
                          <div className="col-sm-6">
                            <TextField
                              name="name"
                              onChange={handleChange}
                              onBlur={handleBlur}
                              value={values.name}
                              size="small"
                              fullWidth
                              label="Name"
                              variant="outlined"
                            />
                          </div>
                        </ListItem>
                        <ListItem className="row">
                          <div className="col-sm-4">
                            <Typography
                              variant="subtitle1"
                              color="text.secondary"
                              component="div"
                            >
                              Gender:
                            </Typography>
                          </div>
                          <div className="col-sm-6">
                            <SearchableDropdown
                              name="gender"
                              label="Gender"
                              options={dropdownOptions?.GENDER}
                              value={getSelectedOptionById(
                                dropdownOptions?.GENDER,
                                values.gender,
                              )}
                              onChange={(selected) =>
                                setFieldValue("gender", selected?.id || "")
                              }
                              onBlur={handleBlur}
                            />
                          </div>
                        </ListItem>
                        <ListItem className="row">
                          <div className="col-sm-4">
                            <Typography
                              variant="subtitle1"
                              color="text.secondary"
                              component="div"
                            >
                              Physical Status:
                            </Typography>
                          </div>
                          <div className="col-sm-6">
                            <SearchableDropdown
                              name="physical_status"
                              label="Physical Status"
                              options={dropdownOptions?.PHYSICAL_STATUS}
                              value={getSelectedOptionById(
                                dropdownOptions?.PHYSICAL_STATUS,
                                values.physical_status,
                              )}
                              onChange={(selected) =>
                                setFieldValue(
                                  "physical_status",
                                  selected?.id || "",
                                )
                              }
                              onBlur={handleBlur}
                            />
                          </div>
                        </ListItem>
                        <ListItem className="row">
                          <div className="col-sm-4">
                            <Typography
                              variant="subtitle1"
                              color="text.secondary"
                              component="div"
                            >
                              Height (In CM):
                            </Typography>
                          </div>
                          <div className="col-sm-6">
                            <TextField
                              InputProps={{
                                endAdornment: (
                                  <InputAdornment
                                    name="height"
                                    position="start"
                                  >
                                    cms
                                  </InputAdornment>
                                ),
                              }}
                              size="small"
                              fullWidth
                              name="height"
                              value={values.height}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              label="Height"
                              variant="outlined"
                            />
                          </div>
                        </ListItem>
                        <ListItem className="row">
                          <div className="col-sm-4">
                            <Typography
                              variant="subtitle1"
                              color="text.secondary"
                              component="div"
                            >
                              Marital Status:
                            </Typography>
                          </div>
                          <div className="col-sm-6">
                            <SearchableDropdown
                              name="marital_status"
                              label="Marital Status"
                              options={dropdownOptions?.MARITAL_STATUS}
                              value={getSelectedOptionById(
                                dropdownOptions?.MARITAL_STATUS,
                                values.marital_status,
                              )}
                              onChange={(selected) =>
                                setFieldValue(
                                  "marital_status",
                                  selected?.id || "",
                                )
                              }
                              onBlur={handleBlur}
                            />
                          </div>
                        </ListItem>
                        <ListItem className="row">
                          <div className="col-sm-4">
                            <Typography
                              variant="subtitle1"
                              color="text.secondary"
                              component="div"
                            >
                              Smoking Habit:
                            </Typography>
                          </div>
                          <div className="col-sm-6">
                            <SearchableDropdown
                              name="smoking_habit"
                              label="Smoking Habit"
                              options={dropdownOptions?.SMOKING}
                              value={getSelectedOptionById(
                                dropdownOptions?.SMOKING,
                                values.smoking_habit,
                              )}
                              onChange={(selected) =>
                                setFieldValue(
                                  "smoking_habit",
                                  selected?.id || "",
                                )
                              }
                              onBlur={handleBlur}
                            />
                          </div>
                        </ListItem>
                        <ListItem className="row">
                          <div className="col-sm-4">
                            <Typography
                              variant="subtitle1"
                              color="text.secondary"
                              component="div"
                            >
                              Drinking Habit:
                            </Typography>
                          </div>
                          <div className="col-sm-6">
                            <SearchableDropdown
                              name="drinking_habit"
                              label="Drinking Habit"
                              options={dropdownOptions?.DRINKING}
                              value={getSelectedOptionById(
                                dropdownOptions?.DRINKING,
                                values.drinking_habit,
                              )}
                              onChange={(selected) =>
                                setFieldValue(
                                  "drinking_habit",
                                  selected?.id || "",
                                )
                              }
                              onBlur={handleBlur}
                            />
                          </div>
                        </ListItem>
                      </List>
                    </div>
                    <div className="col-sm-6">
                      <List>
                        <ListItem className="row">
                          <div className="col-sm-4">
                            <Typography
                              variant="subtitle1"
                              color="text.secondary"
                              component="div"
                            >
                              Surname:
                            </Typography>
                          </div>
                          <div className="col-sm-6">
                            <TextField
                              size="small"
                              name="surname"
                              value={values.surname || ""}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              fullWidth
                              label="Surname"
                              variant="outlined"
                            />
                          </div>
                        </ListItem>
                        <ListItem className="row">
                          <div className="col-sm-4">
                            <Typography
                              variant="subtitle1"
                              color="text.secondary"
                              component="div"
                            >
                              Date Of Birth:
                            </Typography>
                          </div>
                          <div className="col-sm-6">
                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                              <DatePicker
                                name="dob"
                                label="Date Of Birth"
                                disableFuture
                                value={values.dob}
                                onChange={(value) =>
                                  setFieldValue("dob", value, true)
                                }
                                sx={{ width: "100%" }}
                                renderInput={(params) => (
                                  <TextField
                                    name="dob"
                                    size="small"
                                    fullWidth
                                    sx={{
                                      "& .MuiOutlinedInput-root": {
                                        minHeight: 40,
                                      },
                                    }}
                                    {...params}
                                  />
                                )}
                              />
                            </LocalizationProvider>
                          </div>
                        </ListItem>
                        <ListItem className="row">
                          <div className="col-sm-4">
                            <Typography
                              variant="subtitle1"
                              color="text.secondary"
                              component="div"
                            >
                              Body Type:
                            </Typography>
                          </div>
                          <div className="col-sm-6">
                            <SearchableDropdown
                              name="body_type"
                              label="Body Type"
                              options={dropdownOptions?.BODY_TYPES}
                              value={getSelectedOptionById(
                                dropdownOptions?.BODY_TYPES,
                                values.body_type,
                              )}
                              onChange={(selected) =>
                                setFieldValue("body_type", selected?.id || "")
                              }
                              onBlur={handleBlur}
                            />
                          </div>
                        </ListItem>
                        <ListItem className="row">
                          <div className="col-sm-4">
                            <Typography
                              variant="subtitle1"
                              color="text.secondary"
                              component="div"
                            >
                              Weight (In KG):
                            </Typography>
                          </div>
                          <div className="col-sm-6">
                            <TextField
                              InputProps={{
                                endAdornment: (
                                  <InputAdornment position="start">
                                    kgs
                                  </InputAdornment>
                                ),
                              }}
                              size="small"
                              fullWidth
                              name="weight"
                              value={values.weight || ""}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              label="Weight"
                              variant="outlined"
                            />
                          </div>
                        </ListItem>
                        <ListItem className="row">
                          <div className="col-sm-4">
                            <Typography
                              variant="subtitle1"
                              color="text.secondary"
                              component="div"
                            >
                              Mother Tounge:
                            </Typography>
                          </div>
                          {/* <div className="col-sm-6">
                          <Autocomplete
                            disablePortal
                            id="mother_tongue"
                            name="mother_tongue"
                            options={
                              dropdownOptions?.MOTHER_TOUNGE_LIST
                                ? dropdownOptions?.MOTHER_TOUNGE_LIST
                                : []
                            }
                            value={values.mother_tongue || ""}
                            size="small"
                            getOptionLabel={(option) =>
                              option.name ? option.name : ""
                            }
                            fullWidth
                            onChange={handleChange}
                            onBlur={handleBlur}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                name="mother_tongue"
                                label="Language"
                              />
                            )}
                          />
                        </div> */}
                          <div className="col-sm-6">
                            <SearchableDropdown
                              name="mother_tongue"
                              label="Language"
                              className="dropdown-height"
                              options={dropdownOptions?.MOTHER_TOUNGE_LIST}
                              value={getSelectedOptionById(
                                dropdownOptions?.MOTHER_TOUNGE_LIST,
                                values.mother_tongue,
                              )}
                              onChange={(selected) =>
                                setFieldValue(
                                  "mother_tongue",
                                  selected?.id || "",
                                )
                              }
                              onBlur={handleBlur}
                            />
                          </div>
                        </ListItem>
                        <ListItem className="row">
                          <div className="col-sm-4">
                            <Typography
                              variant="subtitle1"
                              color="text.secondary"
                              component="div"
                            >
                              Eating Habit:
                            </Typography>
                          </div>
                          <div className="col-sm-6">
                            <SearchableDropdown
                              name="eating_habit"
                              label="Eating Habit"
                              options={dropdownOptions?.FOOD}
                              value={getSelectedOptionById(
                                dropdownOptions?.FOOD,
                                values.eating_habit,
                              )}
                              onChange={(selected) =>
                                setFieldValue(
                                  "eating_habit",
                                  selected?.id || "",
                                )
                              }
                              onBlur={handleBlur}
                            />
                          </div>
                        </ListItem>
                      </List>
                    </div>
                  </div>
                  <br />
                  <div className="row">
                    <div className="col-sm-6 order-sm-2">
                      <div className="row">
                        <div className="col-sm-12">
                          <Typography gutterBottom variant="h5" component="div">
                            Horoscopic Information
                          </Typography>
                        </div>
                      </div>
                      <List>
                        <ListItem className="row">
                          <div className="col-sm-4">
                            <Typography
                              variant="subtitle1"
                              color="text.secondary"
                              component="div"
                            >
                              Nakshtram:
                            </Typography>
                          </div>
                          {/* <div className="col-sm-6">
                          <Autocomplete
                            disablePortal
                            id="star"
                            name="star"
                            options={
                              dropdownOptions?.STAR_LIST
                                ? dropdownOptions?.STAR_LIST
                                : []
                            }
                            size="small"
                            getOptionLabel={(option) =>
                              option.name ? option.name : ""
                            }
                            fullWidth
                            onChange={(e, v) => {
                              setFieldValue("star", v.id);
                            }}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                name="star"
                                label="Nakshtram"
                              />
                            )}
                          />
                        </div> */}
                          <div className="col-sm-6">
                            <SearchableDropdown
                              name="star"
                              label="Nakshtram"
                              options={dropdownOptions?.STAR_LIST}
                              value={getSelectedOptionById(
                                dropdownOptions?.STAR_LIST,
                                values.star,
                              )}
                              onChange={(selected) =>
                                setFieldValue("star", selected?.id || "")
                              }
                              onBlur={handleBlur}
                            />
                          </div>
                        </ListItem>
                        <ListItem className="row">
                          <div className="col-sm-4">
                            <Typography
                              variant="subtitle1"
                              color="text.secondary"
                              component="div"
                            >
                              Raasi:
                            </Typography>
                          </div>
                          {/* <div className="col-sm-6">
                          <Autocomplete
                            disablePortal
                            id="zodiac"
                            name="zodiac"
                            options={
                              dropdownOptions?.ZODIAC_LIST
                                ? dropdownOptions?.ZODIAC_LIST
                                : []
                            }
                            size="small"
                            getOptionLabel={(option) =>
                              option.name ? option.name : ""
                            }
                            fullWidth
                            onChange={(e, v) => {
                              setFieldValue("zodiac", v.id);
                            }}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                name="zodiac"
                                label="Raasi"
                              />
                            )}
                          />
                        </div> */}
                          <div className="col-sm-6">
                            <SearchableDropdown
                              name="zodiac"
                              label="Raasi"
                              options={dropdownOptions?.ZODIAC_LIST}
                              value={getSelectedOptionById(
                                dropdownOptions?.ZODIAC_LIST,
                                values.zodiac,
                              )}
                              onChange={(selected) =>
                                setFieldValue("zodiac", selected?.id || "")
                              }
                              onBlur={handleBlur}
                            />
                          </div>
                        </ListItem>
                      </List>
                    </div>
                    <div className="col-sm-6 order-sm-1">
                      <div className="row">
                        <div className="col-sm-12">
                          <Typography gutterBottom variant="h5" component="div">
                            Religion Information
                          </Typography>
                        </div>
                      </div>
                      <List>
                        <ListItem className="row">
                          <div className="col-sm-4">
                            <Typography
                              variant="subtitle1"
                              color="text.secondary"
                              component="div"
                            >
                              Religion:
                            </Typography>
                          </div>
                          {/* <div className="col-sm-6">
                          <Autocomplete
                            disablePortal
                            id="religion"
                            name="religion"
                            options={
                              dropdownOptions?.RELIGION
                                ? dropdownOptions?.RELIGION
                                : []
                            }
                            value={values.religion}
                            size="small"
                            getOptionLabel={(option) =>
                              option.name ? option.name : ""
                            }
                            fullWidth
                            onChange={(e, v) => {
                              setFieldValue("religion", v.id);
                            }}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                name="religion"
                                label="Religion"
                              />
                            )}
                          />
                        </div> */}
                          <div className="col-sm-6">
                            <SearchableDropdown
                              name="religion"
                              label="Religion"
                              options={religionsList}
                              value={getSelectedOptionById(
                                religionsList,
                                values.religion,
                              )}
                              onChange={(selected) => {
                                setFieldValue("religion", selected?.id || "");
                                setFieldValue("caste", "");
                              }}
                              onBlur={handleBlur}
                            />
                          </div>
                        </ListItem>
                        <ListItem className="row">
                          <div className="col-sm-4">
                            <Typography
                              variant="subtitle1"
                              color="text.secondary"
                              component="div"
                            >
                              Caste:
                            </Typography>
                          </div>
                          <div className="col-sm-6">
                            <SearchableDropdown
                              name="caste"
                              label="Caste"
                              options={casteOptions}
                              value={getSelectedOptionById(
                                casteOptions,
                                values.caste,
                              )}
                              onChange={(selected) =>
                                setFieldValue("caste", selected?.id || "")
                              }
                              onBlur={handleBlur}
                              disabled={!values.religion}
                              noOptionsText={
                                values.religion
                                  ? "No matching caste found"
                                  : "Select religion first"
                              }
                            />
                          </div>
                        </ListItem>

                        <ListItem className="row">
                          <div className="col-sm-4">
                            <Typography
                              variant="subtitle1"
                              color="text.secondary"
                              component="div"
                            >
                              Time of Birth:
                            </Typography>
                          </div>
                          <div className="col-sm-6">
                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                              <TimePicker
                                label="Time of Birth"
                                ampm={true}
                                format="hh:mm A"
                                timeSteps={{ minutes: 1 }}
                                open={isTimeOfBirthPickerOpen}
                                onOpen={() => setIsTimeOfBirthPickerOpen(true)}
                                onClose={() =>
                                  setIsTimeOfBirthPickerOpen(false)
                                }
                                onChange={(value) =>
                                  setFieldValue("time_of_birth", value, true)
                                }
                                value={values.time_of_birth || null}
                                slotProps={{
                                  textField: {
                                    size: "small",
                                    name: "time_of_birth",
                                    fullWidth: true,
                                    placeholder: "Time of Birth",
                                    onClick: () =>
                                      setIsTimeOfBirthPickerOpen(true),
                                  },
                                }}
                              />
                            </LocalizationProvider>
                          </div>
                        </ListItem>
                      </List>
                    </div>
                  </div>
                  <br />
                  <div className="row">
                    <div className="col-sm-6">
                      <div className="row">
                        <div className="col-sm-12">
                          <Typography gutterBottom variant="h5" component="div">
                            Location Information
                          </Typography>
                        </div>
                      </div>
                      <List>
                        <ListItem className="row">
                          <div className="col-sm-4">
                            <Typography
                              variant="subtitle1"
                              color="text.secondary"
                              component="div"
                            >
                              Country:
                            </Typography>
                          </div>
                          {/* <div className="col-sm-6">
                          <Autocomplete
                            disablePortal
                            id="countries"
                            name="countries"
                            options={
                              dropdownOptions?.COUNTRYS
                                ? dropdownOptions?.COUNTRYS
                                : []
                            }
                            size="small"
                            getOptionLabel={(option) =>
                              option.name ? option.name : ""
                            }
                            fullWidth
                            onChange={(e, v) => {
                              setFieldValue("countries", v.id);
                            }}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                name="countries"
                                label="Country"
                              />
                            )}
                          />
                        </div>                        */}
                          <div className="col-sm-6">
                            <SearchableDropdown
                              name="country"
                              label="Country"
                              options={dropdownOptions?.COUNTRYS}
                              value={getSelectedOptionById(
                                dropdownOptions?.COUNTRYS,
                                values.country,
                              )}
                              onChange={(selected) =>
                                setFieldValue("country", selected?.id || "")
                              }
                              onBlur={handleBlur}
                            />
                          </div>
                        </ListItem>
                        <ListItem className="row">
                          <div className="col-sm-4">
                            <Typography
                              variant="subtitle1"
                              color="text.secondary"
                              component="div"
                            >
                              Citizenship:
                            </Typography>
                          </div>
                          <div className="col-sm-6">
                            <SearchableDropdown
                              name="citizen"
                              label="Citizenship"
                              options={dropdownOptions?.COUNTRYS}
                              value={getSelectedOptionById(
                                dropdownOptions?.COUNTRYS,
                                values.citizen,
                              )}
                              onChange={(selected) =>
                                setFieldValue("citizen", selected?.id || "")
                              }
                              onBlur={handleBlur}
                            />
                          </div>
                        </ListItem>
                        <ListItem className="row">
                          <div className="col-sm-4">
                            <Typography
                              variant="subtitle1"
                              color="text.secondary"
                              component="div"
                            >
                              State:
                            </Typography>
                          </div>
                          <div className="col-sm-6">
                            <SearchableDropdown
                              name="state"
                              label="State"
                              options={states?.STATES}
                              value={getSelectedOptionById(
                                states?.STATES,
                                values.state,
                              )}
                              onChange={(selected) => {
                                setFieldValue("state", selected?.id || "");
                                setFieldValue("district", "");
                              }}
                              onBlur={handleBlur}
                            />
                          </div>
                        </ListItem>
                        <ListItem className="row">
                          <div className="col-sm-4">
                            <Typography
                              variant="subtitle1"
                              color="text.secondary"
                              component="div"
                            >
                              District:
                            </Typography>
                          </div>
                          <div className="col-sm-6">
                            <SearchableDropdown
                              name="district"
                              label="District"
                              options={districtOptions}
                              value={getSelectedOptionById(
                                districtOptions,
                                values.district,
                              )}
                              onChange={(selected) =>
                                setFieldValue("district", selected?.id || "")
                              }
                              onBlur={handleBlur}
                              disabled={!values.state}
                              noOptionsText={
                                values.state
                                  ? "No matching district found"
                                  : "Select state first"
                              }
                            />
                          </div>
                        </ListItem>
                        <ListItem className="row">
                          <div className="col-sm-4">
                            <Typography
                              variant="subtitle1"
                              color="text.secondary"
                              component="div"
                            >
                              Town/City:
                            </Typography>
                          </div>
                          <div className="col-sm-6">
                            <TextField
                              name="city"
                              onChange={handleChange}
                              onBlur={handleBlur}
                              value={values.city}
                              size="small"
                              fullWidth
                              label="Town/City"
                              variant="outlined"
                            />
                          </div>
                        </ListItem>
                      </List>
                    </div>
                    <div className="col-sm-6">
                      <div className="row">
                        <div className="col-sm-12">
                          <Typography gutterBottom variant="h5" component="div">
                            Professional Information
                          </Typography>
                        </div>
                      </div>
                      <List>
                        <ListItem className="row">
                          <div className="col-sm-4">
                            <Typography
                              variant="subtitle1"
                              color="text.secondary"
                              component="div"
                            >
                              Higher Qualification:
                            </Typography>
                          </div>
                          <div className="col-sm-6">
                            <SearchableDropdown
                              name="education"
                              label="Higher Qualification"
                              options={dropdownOptions?.EDUCATION}
                              value={getSelectedOptionById(
                                dropdownOptions?.EDUCATION,
                                values.education,
                              )}
                              onChange={(selected) =>
                                setFieldValue("education", selected?.id || "")
                              }
                              onBlur={handleBlur}
                            />
                          </div>
                        </ListItem>
                        <ListItem className="row">
                          <div className="col-sm-4">
                            <Typography
                              variant="subtitle1"
                              color="text.secondary"
                              component="div"
                            >
                              Employed In:
                            </Typography>
                          </div>
                          <div className="col-sm-6">
                            <TextField
                              name="employeed_in"
                              onChange={handleChange}
                              onBlur={handleBlur}
                              value={values.employeed_in}
                              size="small"
                              fullWidth
                              label="Employed In"
                              variant="outlined"
                            />
                          </div>
                        </ListItem>
                        <ListItem className="row">
                          <div className="col-sm-4">
                            <Typography
                              variant="subtitle1"
                              color="text.secondary"
                              component="div"
                            >
                              Occupation:
                            </Typography>
                          </div>
                          <div className="col-sm-6">
                            <SearchableDropdown
                              name="occupation"
                              label="Occupation"
                              options={dropdownOptions?.OCCUPATION}
                              value={getSelectedOptionById(
                                dropdownOptions?.OCCUPATION,
                                values.occupation,
                              )}
                              onChange={(selected) =>
                                setFieldValue("occupation", selected?.id || "")
                              }
                              onBlur={handleBlur}
                            />
                          </div>
                        </ListItem>
                        <ListItem className="row">
                          <div className="col-sm-4">
                            <Typography
                              variant="subtitle1"
                              color="text.secondary"
                              component="div"
                            >
                              Annual Income:
                            </Typography>
                          </div>
                          <div className="col-sm-6">
                            <SearchableDropdown
                              name="salary"
                              label="Annual Income"
                              options={dropdownOptions?.SALARY}
                              value={getSelectedOptionById(
                                dropdownOptions?.SALARY,
                                values.salary,
                              )}
                              onChange={(selected) =>
                                setFieldValue("salary", selected?.id || "")
                              }
                              onBlur={handleBlur}
                            />
                          </div>
                        </ListItem>
                      </List>
                    </div>
                  </div>
                  <br />
                  <div className="row">
                    <div className="col-sm-12">
                      <Typography gutterBottom variant="h5" component="div">
                        Family Information
                      </Typography>
                    </div>
                    <div className="col-sm-6">
                      <List>
                        <ListItem className="row">
                          <div className="col-sm-4">
                            <Typography
                              variant="subtitle1"
                              color="text.secondary"
                              component="div"
                            >
                              Family Type:
                            </Typography>
                          </div>
                          <div className="col-sm-6">
                            <SearchableDropdown
                              name="family_type"
                              label="Family Type"
                              options={dropdownOptions?.FAMILY_TYPE}
                              value={getSelectedOptionById(
                                dropdownOptions?.FAMILY_TYPE,
                                values.family_type,
                              )}
                              onChange={(selected) =>
                                setFieldValue("family_type", selected?.id || "")
                              }
                              onBlur={handleBlur}
                            />
                          </div>
                        </ListItem>

                        <ListItem className="row">
                          <div className="col-sm-4">
                            <Typography
                              variant="subtitle1"
                              color="text.secondary"
                              component="div"
                            >
                              Fathers Occupation:
                            </Typography>
                          </div>
                          <div className="col-sm-6">
                            <TextField
                              name="fathers_occupation"
                              onChange={handleChange}
                              onBlur={handleBlur}
                              value={values.fathers_occupation}
                              size="small"
                              fullWidth
                              label="Fathers Occupation"
                              variant="outlined"
                            />
                          </div>
                        </ListItem>
                        <ListItem className="row">
                          <div className="col-sm-4">
                            <Typography
                              variant="subtitle1"
                              color="text.secondary"
                              component="div"
                            >
                              Number of Brothers:
                            </Typography>
                          </div>
                          <div className="col-sm-6">
                            <TextField
                              name="no_of_brothers"
                              onChange={handleChange}
                              onBlur={handleBlur}
                              value={values.no_of_brothers}
                              size="small"
                              fullWidth
                              label="Number of Brothers"
                              variant="outlined"
                            />
                          </div>
                        </ListItem>
                        <ListItem className="row">
                          <div className="col-sm-4">
                            <Typography
                              variant="subtitle1"
                              color="text.secondary"
                              component="div"
                            >
                              Number of Brothers Married:
                            </Typography>
                          </div>
                          <div className="col-sm-6">
                            <TextField
                              name="no_of_brothers_married"
                              onChange={handleChange}
                              onBlur={handleBlur}
                              value={values.no_of_brothers_married}
                              size="small"
                              fullWidth
                              label="Number of Brothers Married"
                              variant="outlined"
                            />
                          </div>
                        </ListItem>
                      </List>
                    </div>
                    <div className="col-sm-6">
                      <List>
                        <ListItem className="row">
                          <div className="col-sm-4">
                            <Typography
                              variant="subtitle1"
                              color="text.secondary"
                              component="div"
                            >
                              Family Status:
                            </Typography>
                          </div>
                          <div className="col-sm-6">
                            <SearchableDropdown
                              name="family_status"
                              label="Family Status"
                              options={dropdownOptions?.FAMILY_STATUS}
                              value={getSelectedOptionById(
                                dropdownOptions?.FAMILY_STATUS,
                                values.family_status,
                              )}
                              onChange={(selected) =>
                                setFieldValue(
                                  "family_status",
                                  selected?.id || "",
                                )
                              }
                              onBlur={handleBlur}
                            />
                          </div>
                        </ListItem>
                        <ListItem className="row">
                          <div className="col-sm-4">
                            <Typography
                              variant="subtitle1"
                              color="text.secondary"
                              component="div"
                            >
                              Mothers Occupation:
                            </Typography>
                          </div>
                          <div className="col-sm-6">
                            <TextField
                              name="mothers_occupation"
                              onChange={handleChange}
                              onBlur={handleBlur}
                              value={values.mothers_occupation}
                              size="small"
                              fullWidth
                              label="Mothers Occupation"
                              variant="outlined"
                            />
                          </div>
                        </ListItem>
                        <ListItem className="row">
                          <div className="col-sm-4">
                            <Typography
                              variant="subtitle1"
                              color="text.secondary"
                              component="div"
                            >
                              Number of Sisters:
                            </Typography>
                          </div>
                          <div className="col-sm-6">
                            <TextField
                              name="no_of_sisters"
                              onChange={handleChange}
                              onBlur={handleBlur}
                              value={values.no_of_sisters || ""}
                              size="small"
                              fullWidth
                              label="Number of Sisters"
                              variant="outlined"
                            />
                          </div>
                        </ListItem>
                        <ListItem className="row">
                          <div className="col-sm-4">
                            <Typography
                              variant="subtitle1"
                              color="text.secondary"
                              component="div"
                            >
                              Number of Sisters Married:
                            </Typography>
                          </div>
                          <div className="col-sm-6">
                            <TextField
                              name="no_of_sisters_married"
                              onChange={handleChange}
                              onBlur={handleBlur}
                              value={values.no_of_sisters_married || ""}
                              size="small"
                              fullWidth
                              label="Number of Sisters Married"
                              variant="outlined"
                            />
                          </div>
                        </ListItem>
                      </List>
                    </div>
                  </div>
                  <br />
                  <div className="row">
                    <div className="col-sm-12">
                      <Typography gutterBottom variant="h5" component="div">
                        Other Information
                      </Typography>
                    </div>
                    <div className="col-sm-6">
                      <List>
                        <ListItem className="row">
                          <div className="col-sm-4">
                            <Typography
                              variant="subtitle1"
                              color="text.secondary"
                              component="div"
                            >
                              Hobbies:
                            </Typography>
                          </div>
                          <div className="col-sm-6">
                            <TextField
                              name="hobbies"
                              size="small"
                              onChange={handleChange}
                              onBlur={handleBlur}
                              value={values.hobbies}
                              fullWidth
                              label="Hobbies"
                              variant="outlined"
                            />
                          </div>
                        </ListItem>
                      </List>
                    </div>
                    <div className="col-sm-6">
                      <List>
                        <ListItem className="row">
                          <div className="col-sm-4">
                            <Typography
                              variant="subtitle1"
                              color="text.secondary"
                              component="div"
                            >
                              Interests:
                            </Typography>
                          </div>
                          <div className="col-sm-6">
                            <TextField
                              name="interests"
                              size="small"
                              onChange={handleChange}
                              onBlur={handleBlur}
                              value={values.interests}
                              fullWidth
                              label="Interests"
                              variant="outlined"
                            />
                          </div>
                        </ListItem>
                      </List>
                    </div>
                  </div>
                  <br />
                  <div className="row">
                    <div className="col-sm-12">
                      <Typography gutterBottom variant="h5" component="div">
                        About Me
                      </Typography>
                    </div>
                    <div className="col-sm-12">
                      <TextField
                        name="about_me"
                        onChange={handleChange}
                        onBlur={handleBlur}
                        size="small"
                        fullWidth
                        rows={4}
                        value={values.about_me}
                        label="About me"
                        placeholder="Describe yourself"
                        multiline
                      />
                    </div>
                  </div>
                  <br />
                </form>
              );
            }}
          </Formik>
        ) : (
          ""
        )}
      </div>
      <Loading
        styles={{ top: 0, left: 0, right: 0, width: "100%" }}
        loading={loading}
      />
      <ToastContainer />
    </div>
  );
};
export default EditProfile;
